import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Breadcrumbs, Paper, Typography } from '@mui/material';

import { Tabs, TabsContent, PageTabsList, PageTabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ProfileSettings } from '@/components/dashboard/ProfileSettings';
import { AvailabilityCalendar } from '@/components/dashboard/AvailabilityCalendar';
import FeedbackPanel, { FeedbackContext, FeedbackPayload } from '@/components/feedback/FeedbackPanel';
import { submitFeedback } from '@/lib/apiService';
import { showErrorToast, showSuccessToast } from '@/utils/toast-utils';
import SettingsHub from '@/components/settings/SettingsHub';
import ProfileDetailsView from '@/components/settings/ProfileDetailsView';
import PlansAndBillingView from '@/components/settings/PlansAndBillingView';
import AvailabilityView from '@/components/settings/AvailabilityView';
import NotificationPreferencesView from '@/components/settings/NotificationPreferencesView';
import SecuritySettingsView from '@/components/settings/SecuritySettingsView';
import SubscriptionPlanView from '@/components/settings/SubscriptionPlanView';
import UpgradePlanView from '@/components/settings/UpgradePlanView';
import InvoiceTaxDetailsView from '@/components/settings/InvoiceTaxDetailsView';

type ProfileTab = 'settings' | 'availability' | 'billing' | 'feedback';

const PROFILE_TABS: ProfileTab[] = ['settings', 'availability', 'billing', 'feedback'];

const parseProfileTab = (search: string): ProfileTab | null => {
  const tabParam = new URLSearchParams(search).get('tab');
  if (!tabParam) return null;
  return PROFILE_TABS.includes(tabParam as ProfileTab) ? (tabParam as ProfileTab) : null;
};

interface LegacyProfileManagerProps {
  activeTab: ProfileTab;
  onTabChange: (value: string) => void;
  onGlobalFeedbackSubmit: (payload: FeedbackPayload) => Promise<void>;
}

const LegacyProfileManager: React.FC<LegacyProfileManagerProps> = ({
  activeTab,
  onTabChange,
  onGlobalFeedbackSubmit,
}) => {
  return (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid rgba(15, 23, 42, 0.08)',
          bgcolor: '#fff',
        }}
      >
        <div className="space-y-6 p-4 sm:p-5 lg:px-7 lg:pb-7 lg:pt-6">
          <Box>
            <Paper
              elevation={0}
              sx={{
                borderRadius: '14px',
                border: '1px solid #dbe6eb',
                bgcolor: '#f8fafc',
                px: { xs: 2, sm: 3 },
                py: 1.25,
              }}
            >
              <Breadcrumbs separator="/" aria-label="breadcrumb">
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#0f172a' }}>
                  Settings
                </Typography>
              </Breadcrumbs>
            </Paper>
          </Box>

          <div>
            <h1 className="text-3xl font-bold text-cs-foreground">My Profile</h1>
            <p className="mt-2 text-gray-600">Manage your personal information, availability, and billing details</p>
          </div>

          <Tabs value={activeTab} onValueChange={onTabChange} className="w-full">
            <PageTabsList className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2 md:w-[880px] lg:grid-cols-4">
              <PageTabsTrigger value="settings" className="min-h-[56px] px-4 text-sm">
                My Settings
              </PageTabsTrigger>
              <PageTabsTrigger value="availability" className="min-h-[56px] px-4 text-sm">
                My Availability
              </PageTabsTrigger>
              <PageTabsTrigger value="billing" className="min-h-[56px] px-4 text-sm">
                My Billing Profile
              </PageTabsTrigger>
              <PageTabsTrigger value="feedback" className="min-h-[56px] px-4 text-sm">
                Feedback
              </PageTabsTrigger>
            </PageTabsList>

            <TabsContent value="settings" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Personal Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <ProfileSettings />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="availability" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Calendar Availability</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="mb-4 text-gray-700">
                    <p className="font-medium">Set your available dates for client appointments.</p>
                    <p className="mt-1 text-sm text-gray-600">
                      Select dates when you are available to meet with clients. Click on a date to toggle selection.
                    </p>
                  </div>
                  <AvailabilityCalendar />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="billing" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Billing Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="mb-4 text-gray-700">
                    <p className="font-medium">In this section, you can update the plans you have with your clients</p>
                  </div>
                  <SubscriptionPlanView />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="feedback" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Global / Overall Product Feedback</CardTitle>
                </CardHeader>
                <CardContent>
                  <FeedbackPanel
                    title="Overall Usability Feedback"
                    description="Please provide feedback on the overall usability of the application."
                    titlePlaceholder="e.g., Navigation and layout thoughts"
                    detailsPlaceholder="Share detailed feedback about the overall product experience."
                    detailsRows={6}
                    submitLabel="Submit Product Feedback"
                    defaultContext={FeedbackContext.NUTRITIONIST_PROFILE}
                    variant="inline"
                    onSubmit={onGlobalFeedbackSubmit}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </Paper>
    </Box>
  );
};

const MyProfilePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const tabFromQuery = useMemo(() => parseProfileTab(location.search), [location.search]);
  const currentView = useMemo(() => new URLSearchParams(location.search).get('view'), [location.search]);
  const showSettingsHub = useMemo(() => currentView === 'hub', [currentView]);
  const showProfileDetails = useMemo(
    () => currentView === 'profile-details' || currentView === 'practice-info',
    [currentView],
  );
  const showNotificationPreferences = useMemo(() => currentView === 'notifications', [currentView]);
  const showSecuritySettings = useMemo(() => currentView === 'security', [currentView]);
  const showPlansPackages = useMemo(() => currentView === 'plans-packages', [currentView]);
  const showSubscriptionPlan = useMemo(() => currentView === 'subscription-plan', [currentView]);
  const showUpgradePlan = useMemo(() => currentView === 'upgrade-plan', [currentView]);
  const showInvoiceTaxDetails = useMemo(() => currentView === 'invoice-tax-details', [currentView]);
  const [activeTab, setActiveTab] = useState<ProfileTab>('settings');

  useEffect(() => {
    if (tabFromQuery) {
      setActiveTab(tabFromQuery);
    }
  }, [tabFromQuery]);

  const handleGlobalFeedbackSubmit = async (payload: FeedbackPayload) => {
    const numericRating = payload.rating ? Number(payload.rating) : undefined;

    try {
      await submitFeedback({
        feedbackType: payload.context,
        title: payload.title,
        feedbackText: payload.details,
        rating: Number.isFinite(numericRating || NaN) ? numericRating : undefined,
        passFail: payload.result || undefined,
        feedbackDate: new Date().toISOString().split('T')[0],
      });

      showSuccessToast('Feedback submitted', 'Thanks for sharing your thoughts about the product.');
    } catch (error) {
      console.error('Failed to submit global feedback', error);
      showErrorToast(error, 'Feedback submission failed');
      throw error;
    }
  };

  const handleLegacyTabChange = (value: string) => {
    if (!PROFILE_TABS.includes(value as ProfileTab)) {
      return;
    }

    const nextTab = value as ProfileTab;
    setActiveTab(nextTab);

    const queryParams = new URLSearchParams(location.search);
    queryParams.set('tab', nextTab);
    navigate({ pathname: '/profile', search: queryParams.toString() }, { replace: true });
  };

  if (!tabFromQuery && showSettingsHub) {
    return (
      <SettingsHub
        onOpenTab={(tab) => navigate(`/profile?tab=${tab}`)}
        onOpenProfileDetails={() => navigate('/profile?view=profile-details')}
        onOpenNotificationPreferences={() => navigate('/profile?view=notifications')}
        onOpenSecurity={() => navigate('/profile?view=security')}
        onNavigateInvoicing={() => navigate('/profile?view=invoice-tax-details')}
        onOpenPlansPackages={() => navigate('/profile?view=plans-packages')}
        onOpenSubscriptionPlan={() => navigate('/profile?view=subscription-plan')}
        onNavigateRecipes={() => navigate('/recipes')}
      />
    );
  }

  if (!tabFromQuery && showProfileDetails) {
    return <ProfileDetailsView />;
  }

  if (!tabFromQuery && showNotificationPreferences) {
    return <NotificationPreferencesView />;
  }

  if (!tabFromQuery && showSecuritySettings) {
    return <SecuritySettingsView />;
  }

  if (!tabFromQuery && showPlansPackages) {
    return <PlansAndBillingView />;
  }

  if (!tabFromQuery && showSubscriptionPlan) {
    return <SubscriptionPlanView />;
  }

  if (!tabFromQuery && showUpgradePlan) {
    return <UpgradePlanView />;
  }

  if (!tabFromQuery && showInvoiceTaxDetails) {
    return <InvoiceTaxDetailsView />;
  }

  if (!tabFromQuery || tabFromQuery === 'settings') {
    return <ProfileDetailsView />;
  }

  if (tabFromQuery === 'availability') {
    return <AvailabilityView />;
  }

  if (tabFromQuery === 'billing') {
    return <SubscriptionPlanView />;
  }

  return (
    <LegacyProfileManager
      activeTab={activeTab}
      onTabChange={handleLegacyTabChange}
      onGlobalFeedbackSubmit={handleGlobalFeedbackSubmit}
    />
  );
};

export default MyProfilePage;
